import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { List, ListItem, ListItemText, Typography } from '@mui/material';
import { usePrevious } from 'services/hooks';
import type { TagStore } from 'services/store/tags';
import type { TagType } from 'services/api/responses/responseTypes';
import { TagDrawerContainer } from '../TagDrawer';
import useStyles from './style';

type TagData = { type: TagType; name: string };

type PropsType = {
  tagStore: TagStore;
  getTagList: (tagType: TagType) => void;
};

const SideBar = (props: PropsType) => {
  const { tagStore, getTagList } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const [selectedTagType, setSelectedTagType] = useState(null);

  const previouslySelectedTagType = usePrevious(selectedTagType);

  const [isTagDrawerOpen, setIsTagDrawerOpen] = useState(false);

  const tagData: Array<TagData> = [
    { type: 'moods', name: t('tags:type:mood') },
    { type: 'genres', name: t('tags:type:genre') },
    { type: 'vocals', name: t('tags:type:vocal_plural') },
    {
      type: 'instruments',
      name: t('tags:type:instrument_plural'),
    },
    { type: 'keys', name: t('tags:type:key') },
    { type: 'energies', name: t('tags:type:energy') },
    { type: 'eras', name: t('tags:type:era') },
  ];

  const isTagTypeSelected = (tagType: TagType): boolean =>
    selectedTagType === tagType;

  const getTagListIfNeeded = (tagType: TagType) => {
    // if the tag array for the given tagType exist already
    // in the tags map we don't need to fetch it again
    if (tagStore[tagType] != null) return;
    getTagList(tagType);
  };
  const getCurrentTagType = (): TagType =>
    // @ts-ignore
    selectedTagType || previouslySelectedTagType;

  const selectTagType = (tagType: TagType) => () => {
    const isSelected = isTagTypeSelected(tagType);
    getTagListIfNeeded(tagType);
    // @ts-ignore
    setSelectedTagType(isSelected ? null : tagType);
    setIsTagDrawerOpen(!isSelected);
  };

  return (
    <div className={classes.root}>
      <div className={classes.listContainer}>
        <div className={classes.list}>
          <List className={classes.list} data-testid="filters-list">
            {tagData.map((data: TagData) => (
              <ListItem
                role="listitem"
                data-testid={`filters-list-item-${data.type}`}
                className={classes.listItem}
                button
                key={data.name}
                onClick={selectTagType(data.type)}
                selected={isTagTypeSelected(data.type)}
              >
                <ListItemText>
                  <Typography component="h2" variant="h6">
                    {data.name}
                  </Typography>
                </ListItemText>
              </ListItem>
            ))}
          </List>
        </div>
        <div className={classes.tagDrawer}>
          <TagDrawerContainer
            isOpen={isTagDrawerOpen}
            tagType={getCurrentTagType()}
          />
        </div>
      </div>
    </div>
  );
};

export default SideBar;
